import { useEffect, useRef, useCallback } from 'react';
import { useContext } from 'react';
import AuthContext from '../contexts/AuthContext';
import { useToast } from '../components/main/ToastContainer';
import { getSpecificData } from '../helpers/store_data';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const useDeviceMonitoring = () => {
  const { user } = useContext(AuthContext);
  const { showToast } = useToast();
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const errorCountRef = useRef(0);
  const lastErrorToastRef = useRef(0);
  const isPollingRef = useRef(false);
  const pollIntervalRef = useRef(30000); // Default 30 seconds

  const handleConnection = useCallback(() => {
    console.log('Device monitoring polling started');
    errorCountRef.current = 0;
  }, []);

  const handleDeviceNotification = useCallback((data: any) => {
    if (data.device && data.device.name) {
      showToast(`Device ${data.device.name} is disconnected`, 'warning', 10000);
    }
  }, [showToast]);

  const handleError = useCallback((error: any) => {
    console.error('Device monitoring error:', error);
    errorCountRef.current += 1;
    
    const now = Date.now();
    const timeSinceLastErrorToast = now - lastErrorToastRef.current;
    
    // Only show error toast for first error or after 30 seconds
    if (errorCountRef.current === 1 || timeSinceLastErrorToast > 30000) {
      let errorMessage = 'Device monitoring error';
      
      if (error && error.message) {
        errorMessage = `Monitoring error: ${error.message}`;
      }
      
      showToast(errorMessage, 'error', 5000);
      lastErrorToastRef.current = now;
    }
  }, [showToast]);

  const pollDeviceStatus = useCallback(async () => {
    try {
      const token = user.token || getSpecificData('token');
      
      if (!token) {
        console.warn('No authentication token available for device monitoring');
        return;
      }
      
      const pollUrl = `${API_BASE_URL}/api-v1/device-monitoring/poll/`;
      
      const response = await fetch(pollUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();

      if (data.type === 'device_status') {
        if (data.poll_interval_seconds) {
          pollIntervalRef.current = data.poll_interval_seconds * 1000;
        }
        
        if (data.notifications && data.notifications.length > 0) {
          data.notifications.forEach((notification: any) => {
            handleDeviceNotification(notification);
          });
        }
        
        errorCountRef.current = 0;
        
      } else if (data.type === 'error') {
        handleError(data);
      }
      
    } catch (error) {
      console.error('Error polling device status:', error);
      handleError(error);
    }
  }, [user, handleDeviceNotification, handleError]);

  // Start polling on mount
  useEffect(() => {
    const startPolling = () => {
      if (isPollingRef.current) return;
      
      isPollingRef.current = true;
      handleConnection();
      
      // Initial poll
      pollDeviceStatus();
      
      // Set up recurring polls
      const scheduleNextPoll = () => {
        if (isPollingRef.current) {
          pollTimeoutRef.current = setTimeout(() => {
            pollDeviceStatus().finally(() => {
              scheduleNextPoll();
            });
          }, pollIntervalRef.current);
        }
      };
      
      scheduleNextPoll();
    };

    // Only start polling if user is authenticated
    if (user.is_login && user.token) {
      startPolling();
    }

    // Cleanup on unmount
    return () => {
      isPollingRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [user, handleConnection, pollDeviceStatus]);

  // Return cleanup function for manual control
  return {
    stopPolling: () => {
      isPollingRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    },
    startPolling: () => {
      if (!isPollingRef.current && user.is_login && user.token) {
        isPollingRef.current = true;
        handleConnection();
        pollDeviceStatus();
      }
    }
  };
};